import { useCallback } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import { useSearchParams } from './useSearchParams'

export function usePagination({
  defaultLimit = 8,
}: { defaultLimit?: number } = {}) {
  const { searchParams, updateSearchParam } = useSearchParams()
  const limit = Number(searchParams.get('limit') ?? defaultLimit)
  const offset = Number(searchParams.get('offset') ?? 0)
  const search = searchParams.get('search') ?? ''
  const status = searchParams.get('status') ?? ''
  const sort = searchParams.get('sort') ?? ''
  const debounchedSearch = useDebounce(search, 300)

  const onSearch = useCallback(
    (value: string) => {
      updateSearchParam('search', value)
      updateSearchParam('offset', '0')
    },
    [updateSearchParam],
  )
  const onStatusChange = useCallback(
    (value: string) => {
      updateSearchParam('status', value)
      updateSearchParam('offset', '0')
    },
    [updateSearchParam],
  )
  const onSortChange = useCallback(
    (value: string) => {
      updateSearchParam('sort', value)
      updateSearchParam('offset', '0')
    },
    [updateSearchParam],
  )

  const onNext = useCallback(() => {
    const nextOffset = Number(offset) + Number(limit)
    updateSearchParam('offset', String(nextOffset))
  }, [limit, offset, updateSearchParam])

  const onPrev = useCallback(() => {
    const prevOffset = Number(offset) - Number(limit)
    updateSearchParam('offset', String(prevOffset))
  }, [limit, offset, updateSearchParam])

  const goToPage = useCallback(
    (page: number) => {
      const newOffset = page * limit
      updateSearchParam('offset', String(newOffset))
    },
    [limit, updateSearchParam],
  )

  return {
    search,
    debounchedSearch,
    onSearch,
    status,
    onStatusChange,
    sort,
    onSortChange,
    limit,
    offset,
    onNext,
    onPrev,
    goToPage,
  }
}
